import api from '../api'
import { CORRECTION_PAGE_SIZE, normalizeCorrectionPage, type ClockCorrectionPage } from './correctionPage'

export type ClockKind = 'in' | 'out' | 'pause_start' | 'pause_end'
export type ClockState = 'off' | 'working' | 'paused'

export interface ClockPauseReminder {
  date: string
  timezone: string
  event: string
  worked_minutes: number
  pause_minutes: number
  required_pause_minutes: number
  short_minutes: number
}

export interface ClockEvent {
  id: string
  kind: ClockKind
  occurred_at: string
  source: 'web' | 'mobile' | 'kiosk' | 'auto' | 'correction'
  location_result: 'not_checked' | 'inside' | 'outside' | 'unavailable'
  work_location: string | null
  distance_m: number | null
  note: string
  replaced: boolean
  /** The latest manager decision on a punch outside every work location (older servers omit it). */
  flag_review?: LocationFlagReview | null
}

export interface ClockStatus {
  timezone: string
  server_now: string
  worked_seconds: number
  pause_seconds: number
  pause_policy: Pick<
    ClockPolicy,
    'pause_after_minutes' | 'pause_minutes' | 'long_pause_after_minutes' | 'long_pause_minutes'
  >
  state: ClockState
  since: string | null
  session_started_at: string | null
  allowed: ClockKind[]
  today: {
    date: string
    worked_minutes: number
    pause_minutes: number
    planned_minutes: number
    absence: string | null
    required_pause_minutes: number
    pauses: [string, string | null][]
    first_in: string | null
    events: ClockEvent[]
  }
  yesterday_auto_closed: boolean
  /** Read-only next-day reminder. Older servers omit it. */
  previous_day_pause_reminder?: ClockPauseReminder | null
  last_event: ClockEvent | null
  geofence: 'off' | 'flag'
  /** Today's plan from Planning attendance (older servers omit it). */
  work_mode?: 'present_office' | 'present_remote' | 'present_uni' | null
  event?: ClockEvent
}

export interface ClockDay {
  date: string
  first_in: string | null
  last_out: string | null
  worked_minutes: number
  pause_minutes: number
  planned_minutes: number
  absence: string | null
  required_pause_minutes: number
  pause_short_minutes: number
  overtime_minutes: number
  open: boolean
  auto_closed?: boolean
  outside: boolean
  pending_corrections: number
  status:
    | 'planned'
    | 'absence'
    | 'off'
    | 'in_progress'
    | 'correction_pending'
    | 'auto_closed'
    | 'missing'
    | 'not_started'
    | 'pause_short'
    | 'complete'
  events: ClockEvent[]
  pauses: [string, string | null][]
}

export interface ClockConfirmation {
  status: 'open' | 'changed' | 'confirmed' | 'approved'
  confirmed_at: string | null
  approved_at: string | null
  approved_by: string | null
  worked_minutes?: number
}

export interface ClockWeek {
  fingerprint: string
  supports_review_version?: boolean
  pause_policy: ClockStatus['pause_policy']
  timezone: string
  week_start: string
  days: ClockDay[]
  totals: {
    worked_minutes: number
    planned_minutes: number
    pause_minutes: number
    overtime_minutes: number
    pending_corrections: number
  }
  confirmation: ClockConfirmation
  requires_confirmation: boolean
  any_open: boolean
  employee: { id: string; name: string }
}

export interface ClockCorrection {
  timezone: string
  id: string
  employee: string
  employee_name: string
  requested_by: string | null
  action: 'add' | 'change' | 'remove'
  kind: ClockKind | ''
  original_at: string | null
  proposed_at: string | null
  approved_at: string | null
  reason: string
  status: 'pending' | 'approved' | 'rejected'
  decided_by: string | null
  decided_at: string | null
  decision_note: string
  created_at: string
  /** Manager inbox only: facts for the day the request concerns (older servers omit it). */
  context?: ClockCorrectionContext
}
export interface ClockCorrectionContext {
  day: string
  /** This request's number this calendar month for the employee, counting from 1. */
  request_number: number
  /** Planning attendance for that day, if any. */
  attendance: string | null
  planned_minutes: number
}

export interface ClockPolicy {
  timezone: string
  id: string | null
  country: string
  pause_after_minutes: number
  pause_minutes: number
  long_pause_after_minutes: number
  long_pause_minutes: number
  geofence_mode: 'off' | 'flag'
  auto_close_after_hours: number
  weekly_confirmation: boolean
}

export type ClockPeriod = Pick<ClockWeek, 'days' | 'totals' | 'timezone' | 'employee' | 'any_open' | 'pause_policy'> & {
  start: string
  end: string
  requires_confirmation?: boolean
  weeks: {
    week_start: string
    confirmation: ClockConfirmation
    review_summary?: {
      worked_minutes: number
      pending_corrections: number
      any_open: boolean
      recorded_in_period: boolean
      closed: boolean
    }
  }[]
}
export const getClockPeriod = async (start: string, end: string, employee?: string): Promise<ClockPeriod> =>
  (await api.get('/time-clock/period/', { params: { start, end, employee } })).data
export interface ClockAuditEntry {
  sequence: number
  recorded_at: string
  action: string
  actor: string
  subject: string
  object_id: string
  details: Record<string, unknown>
  digest: string
  previous_hash: string
  payload_valid: boolean
  /** The entry still points at the stored hash of the entry before it (older servers omit this). */
  linked?: boolean
}
export interface ClockAuditFilters {
  category?: string
  employee?: string
  start?: string
  end?: string
}
export const getClockAudit = async (
  filters: ClockAuditFilters & { before?: number },
): Promise<{ rows: ClockAuditEntry[]; next: number | null; timezone?: string }> =>
  (await api.get('/time-clock/audit/', { params: filters })).data
export const downloadClockAudit = async (filters: ClockAuditFilters) => {
  try {
    const response = await api.get('/time-clock/audit/', {
      responseType: 'blob',
      params: { ...filters, file_type: 'csv' },
    })
    const url = URL.createObjectURL(response.data)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `clock-audit-${filters.start || 'start'}-${filters.end || 'now'}.csv`
    anchor.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  } catch (error) {
    // A blob request returns its JSON error as a blob; expose the code like any other request.
    const data = (error as { response?: { data?: unknown } }).response?.data
    if (data instanceof Blob) {
      try {
        ;(error as { response: { data: unknown } }).response.data = JSON.parse(await data.text())
      } catch {
        // Keep the original error when the body is not JSON.
      }
    }
    throw error
  }
}

export interface WorkLocation {
  id: string
  name: string
  address: string
  latitude: number
  longitude: number
  radius_m: number
  active: boolean
}

/** The viewer's IANA time zone: days and weeks are counted in it. */
export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Amsterdam'

export const getClockStatus = async (): Promise<ClockStatus> =>
  (await api.get('/time-clock/status/', { params: { tz: browserTimeZone() } })).data

export const punchClock = async (payload: {
  kind: ClockKind
  request_id: string
  latitude?: number
  longitude?: number
  accuracy?: number
}): Promise<ClockStatus> => (await api.post('/time-clock/punch/', { ...payload, tz: browserTimeZone() })).data

export const getClockWeek = async (start?: string, employee?: string): Promise<ClockWeek> =>
  (await api.get('/time-clock/week/', { params: { start, employee, tz: browserTimeZone() } })).data

export const confirmClockWeek = async (weekStart: string, fingerprint: string): Promise<ClockWeek> =>
  (await api.post('/time-clock/week/confirm/', { week_start: weekStart, fingerprint, tz: browserTimeZone() })).data

export const getClockCorrections = async (scope: 'mine' | 'team', status?: string): Promise<ClockCorrection[]> =>
  (await api.get('/time-clock/corrections/', { params: { scope, status } })).data

export const getClockCorrectionPage = async (
  scope: 'mine' | 'team',
  status: string,
  page: number,
): Promise<ClockCorrectionPage> =>
  normalizeCorrectionPage(
    (
      await api.get('/time-clock/corrections/', {
        params: { scope, status: status === 'all' ? undefined : status, page, page_size: CORRECTION_PAGE_SIZE },
      })
    ).data,
    page,
  )

export interface ClockCorrectionImpact {
  timezone: string
  as_of: string
  worked_delta_minutes: number
  pause_delta_minutes: number
  open: boolean
  days: {
    date: string
    before_worked_minutes: number
    after_worked_minutes: number
    worked_delta_minutes: number
    before_pause_minutes: number
    after_pause_minutes: number
    pause_delta_minutes: number
    open: boolean
  }[]
}
export const getClockCorrectionImpact = async (id: string, adjustedAt?: string): Promise<ClockCorrectionImpact> =>
  (await api.get(`/time-clock/corrections/${id}/preview/`, { params: { adjusted_at: adjustedAt } })).data

export interface ClockCorrectionDraft {
  action: ClockCorrection['action']
  event?: string
  kind?: ClockKind
  proposed_at?: string
  employee?: string
}
export const previewClockCorrection = async (payload: ClockCorrectionDraft): Promise<ClockCorrectionImpact> =>
  (await api.post('/time-clock/corrections/preview/', payload)).data
export const requestClockCorrection = async (
  payload: ClockCorrectionDraft & { reason: string },
): Promise<ClockCorrection> => (await api.post('/time-clock/corrections/', { ...payload, tz: browserTimeZone() })).data

export const decideClockCorrection = async (
  id: string,
  approve: boolean,
  note = '',
  adjustedAt?: string,
): Promise<ClockCorrection> =>
  (
    await api.post(`/time-clock/corrections/${id}/decide/`, {
      approve,
      note,
      adjusted_at: adjustedAt,
      tz: browserTimeZone(),
    })
  ).data

export const getTeamToday = async () =>
  (await api.get('/time-clock/team/today/', { params: { tz: browserTimeZone() } })).data
export interface TeamClockDay {
  employee: string
  name: string
  state: ClockState
  since: string | null
  first_in: string | null
  worked_minutes: number
  pause_minutes: number
  planned_minutes: number
  outside: boolean
  country: string
  teams: { id: string; name: string }[]
  /** Today's pauses against the configured rule (older servers omit these). */
  timezone?: string
  pauses?: [string, string | null][]
  required_pause_minutes?: number
  pause_status?: TodayPauseStatus | null
  pause_policy?: ClockStatus['pause_policy']
}
export type TodayPauseStatus = 'compliant' | 'too_short' | 'no_pause' | 'pause_due' | 'on_pause' | 'not_required'
/** Whether the signed-in manager can approve this week now, and the server's reason if not. */
export interface ClockApprovalEligibility {
  eligible: boolean
  reason: string | null
}
export type TeamClockWeek = ClockWeek['totals'] & {
  employee: string
  name: string
  /** The exact record version shown; bulk approval sends it back so only reviewed weeks are signed. */
  fingerprint?: string
  approval?: ClockApprovalEligibility
  any_open: boolean
  confirmation: ClockConfirmation
  requires_confirmation: boolean
  issues: number
  outside: boolean
  timezone?: string
  days: TeamRecordDay[]
  country: string
  teams: { id: string; name: string }[]
}
/** Additive manager facts; older servers omit independent correction/open/automatic flags. */
export type TeamRecordDay = Pick<
  ClockDay,
  'date' | 'worked_minutes' | 'pause_minutes' | 'planned_minutes' | 'pause_short_minutes' | 'status'
> &
  Partial<Pick<ClockDay, 'pending_corrections' | 'open' | 'auto_closed'>>
export const getTypedTeamToday = async (): Promise<TeamClockDay[]> => getTeamToday()
export const getTeamWeek = async (start?: string): Promise<{ week_start: string; rows: TeamClockWeek[] }> =>
  (await api.get('/time-clock/team/week/', { params: { start, tz: browserTimeZone() } })).data
export type TeamClockPeriod = Omit<TeamClockWeek, 'confirmation' | 'requires_confirmation'> & {
  timezone: string
  weeks: ClockPeriod['weeks']
}
export const getTeamPeriod = async (
  start: string,
  end: string,
): Promise<{ start: string; end: string; rows: TeamClockPeriod[] }> =>
  (await api.get('/time-clock/team/period/', { params: { start, end } })).data
export const approveTeamWeek = async (employee: string, weekStart: string, fingerprint: string): Promise<ClockWeek> =>
  (
    await api.post('/time-clock/team/week/approve/', {
      employee,
      week_start: weekStart,
      fingerprint,
      tz: browserTimeZone(),
    })
  ).data

export interface BulkApprovalResult {
  employee: string
  name: string | null
  outcome: 'approved' | 'skipped'
  code: string | null
  detail: string
}
export const approveTeamWeeks = async (
  weekStart: string,
  items: { employee: string; fingerprint: string }[],
): Promise<{ week_start: string; approved: number; skipped: number; results: BulkApprovalResult[] }> =>
  (
    await api.post('/time-clock/team/week/approve-batch/', {
      week_start: weekStart,
      items,
      tz: browserTimeZone(),
    })
  ).data

export type LocationFlagDecision = 'acknowledged' | 'approved_remote' | 'check_requested'
export interface LocationFlagReview {
  id: string
  decision: LocationFlagDecision
  note: string
  reviewer: string | null
  created_at: string
}
/** A punch outside every work location. Only the check result and distance exist, never a position. */
export interface LocationFlag {
  event: string
  employee: string
  name: string
  country: string
  teams: { id: string; name: string }[]
  kind: ClockKind
  occurred_at: string
  timezone: string
  source: ClockEvent['source']
  work_location: string | null
  radius_m: number | null
  distance_m: number | null
  accuracy_m: number | null
  status: 'open' | LocationFlagDecision
  review: LocationFlagReview | null
}
export const getTeamFlags = async (): Promise<{
  lookback_days: number
  open: number
  truncated: boolean
  rows: LocationFlag[]
}> => (await api.get('/time-clock/team/flags/')).data
export const reviewLocationFlag = async (
  event: string,
  payload: { decision: LocationFlagDecision; note: string; expected_review: string },
): Promise<LocationFlag> => (await api.post(`/time-clock/team/flags/${event}/review/`, payload)).data

export const getClockPolicies = async (): Promise<ClockPolicy[]> => (await api.get('/time-clock/policy/')).data
export const saveClockPolicy = async (policy: Partial<ClockPolicy>): Promise<ClockPolicy> =>
  (await api.put('/time-clock/policy/', policy)).data
export const getWorkLocations = async (): Promise<WorkLocation[]> => (await api.get('/time-clock/locations/')).data
export const createWorkLocation = async (
  location: Omit<WorkLocation, 'id' | 'active'> & { active?: boolean },
): Promise<WorkLocation> => (await api.post('/time-clock/locations/', location)).data
export const updateWorkLocation = async (id: string, values: Partial<WorkLocation>): Promise<WorkLocation> =>
  (await api.patch(`/time-clock/locations/${id}/`, values)).data
export const deleteWorkLocation = async (id: string) => {
  await api.delete(`/time-clock/locations/${id}/`)
}
export const clockExportUrl = (start: string, end: string) =>
  `/time-clock/export/?start=${start}&end=${end}&tz=${encodeURIComponent(browserTimeZone())}`
export const downloadClockExport = async (
  start: string,
  end: string,
  options?: {
    employee?: string
    scope?: 'mine' | 'team'
    format?: 'csv' | 'xlsx'
    approved_only?: boolean
    search?: string
    country?: string
    team?: string
  },
) => {
  const response = await api.get(clockExportUrl(start, end), {
    responseType: 'blob',
    params: { ...options, format: undefined, file_type: options?.format },
  })
  const url = URL.createObjectURL(response.data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `hours-${start}-${end}.${options?.format || 'csv'}`
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
